import { lazy, type ReactNode, Suspense, useEffect, useRef } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router';
import { LoginPage, RegisterPage } from '@/features/auth/AuthPages';
import { NotesPage } from '@/features/notes/NotesPage';
import { AppShell } from '@/features/shell/AppShell';
import { useSession } from '@/hooks/hooks';
import { useNoteActions } from '@/hooks/useNoteActions';
import { SnackbarProvider } from '@/ui/snackbar';
import { type Services, ServicesProvider } from './services';
import { ThemeProvider } from './theme';

// Code splitting: the editor (TipTap/ProseMirror) is only loaded when needed.
const NoteEditorPage = lazy(() => import('@/features/notes/NoteEditorPage').then((m) => ({ default: m.NoteEditorPage })));
const SharedNotePage = lazy(() => import('@/features/share/SharedNotePage').then((m) => ({ default: m.SharedNotePage })));
const SettingsPage = lazy(() => import('@/features/settings/SettingsPage').then((m) => ({ default: m.SettingsPage })));

const page = (node: ReactNode) => <Suspense fallback={<div className="page" aria-busy="true" />}>{node}</Suspense>;

function RequireAuth({ children }: { children: ReactNode }) {
  const session = useSession();
  const location = useLocation();
  if (!session) return <Navigate to={`/login?next=${encodeURIComponent(location.pathname)}`} replace />;
  return children;
}

function RedirectIfAuthed({ children }: { children: ReactNode }) {
  return useSession() ? <Navigate to="/" replace /> : children;
}

/** PWA shortcut target: /new/text and /new/todo. */
function NewNote() {
  const { kind } = useParams();
  const { create } = useNoteActions();
  const navigate = useNavigate();
  // A ref survives StrictMode's effect double invocation, so only one note is created.
  const started = useRef(false);
  useEffect(() => {
    if (started.current) return;
    started.current = true;
    void create(kind === 'todo' ? 'todo' : 'text').then((n) => navigate(`/note/${n.id}`, { replace: true }));
  }, [kind, create, navigate]);
  return null;
}

export function AppRoutes() {
  return (
    <Routes>
      <Route
        path="/login"
        element={
          <RedirectIfAuthed>
            <LoginPage />
          </RedirectIfAuthed>
        }
      />
      <Route path="/register" element={<RegisterPage />} />
      <Route path="/s/:token" element={page(<SharedNotePage />)} />
      <Route
        element={
          <RequireAuth>
            <AppShell />
          </RequireAuth>
        }
      >
        <Route index element={<NotesPage view="all" />} />
        <Route path="folder/:key" element={<NotesPage view="folder" />} />
        <Route path="shared" element={<NotesPage view="shared" />} />
        <Route path="archive" element={<NotesPage view="archive" />} />
        <Route path="note/:id" element={page(<NoteEditorPage />)} />
        <Route path="new/:kind" element={<NewNote />} />
        <Route path="settings" element={page(<SettingsPage />)} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Route>
    </Routes>
  );
}

/** Root component with all providers. */
export function App({ services, router = true }: { services: Services; router?: boolean }) {
  const content = (
    <ThemeProvider>
      <ServicesProvider services={services}>
        <SnackbarProvider>
          <AppRoutes />
        </SnackbarProvider>
      </ServicesProvider>
    </ThemeProvider>
  );
  return router ? <BrowserRouter>{content}</BrowserRouter> : content;
}
